import { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { store } from './store';
import { auth } from './config/firebase';
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import Landing from './components/pages/Landing';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AuthProvider } from './providers/AuthProvider';
import PayPalProvider from './components/payment/PayPalProvider';
import NotFound from './components/pages/NotFound';
import EmailVerificationAlert from './components/auth/EmailVerificationAlert';

// Lazy load heavy components to reduce initial bundle size
const Dashboard = lazy(() => import('./components/pages/Dashboard'));
const VoiceLibrary = lazy(() => import('./components/pages/VoiceLibrary'));
const FavoriteVoices = lazy(() => import('./components/pages/FavoriteVoices'));
const TextToSpeech = lazy(() => import('./components/pages/TextToSpeech'));
const Settings = lazy(() => import('./components/pages/Settings'));
const AdminDashboard = lazy(() => import('./components/pages/admin/AdminDashboard'));
const UserManagement = lazy(() => import('./components/pages/admin/UserManagement'));
const Generations = lazy(() => import('./components/pages/Generations'));
const About = lazy(() => import('./components/pages/About'));
const Contact = lazy(() => import('./components/pages/Contact'));
const Legal = lazy(() => import('./components/pages/Legal'));
const Blog = lazy(() => import('./components/pages/Blog'));
const BlogPost = lazy(() => import('./components/pages/BlogPost'));
const BuyCredits = lazy(() => import('./components/pages/BuyCredits'));
const BillingHistory = lazy(() => import('./components/pages/BillingHistory'));
const PurchaseHistory = lazy(() => import('./components/pages/PurchaseHistory'));
const AdminTransactions = lazy(() => import('./components/pages/AdminTransactions'));

// Loading component for lazy-loaded routes
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

function AppContent() {
  const [showVerification, setShowVerification] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && !user.emailVerified) {
        setShowVerification(true);
      } else {
        setShowVerification(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 text-gray-900 dark:text-gray-100 transition-colors duration-200">
      {showVerification && auth.currentUser && (
        <EmailVerificationAlert
          user={auth.currentUser}
          onClose={() => setShowVerification(false)}
        />
      )}

      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={
            <div className="min-h-screen">
              <LoginForm />
            </div>
          } />
          <Route path="/register" element={
            <div className="min-h-screen">
              <RegisterForm />
            </div>
          } />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <Dashboard />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          <Route path="/voices" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <VoiceLibrary />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />

          <Route path="/favorites" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <FavoriteVoices />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />

          <Route path="/generations" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <Generations />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          <Route path="/tts" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <TextToSpeech />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <Settings />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          <Route path="/admin" element={
            <ProtectedRoute requireAdmin>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <AdminDashboard />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />

          <Route path="/admin/users" element={
            <ProtectedRoute requireAdmin>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <UserManagement />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/transactions" element={
            <ProtectedRoute requireAdmin>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <AdminTransactions />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          <Route path="/buy-credits" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <BuyCredits />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          <Route path="/billing-history" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <BillingHistory />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          <Route path="/purchases" element={
            <ProtectedRoute>
              <div className="min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-1 pt-16">
                  <div className="flex h-full">
                    <Sidebar />
                    <main className="flex-1 p-4 pt-8">
                      <PurchaseHistory />
                    </main>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          } />
          
          {/* 404 - Not Found - Catch-all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ErrorBoundary>
        <Router>
          <HelmetProvider>
            <AuthProvider>
              <PayPalProvider>
                <AppContent />
                <SpeedInsights />
                <Analytics />
              </PayPalProvider>
            </AuthProvider>
          </HelmetProvider>
        </Router>
      </ErrorBoundary>
    </Provider>
  );
}

export default App;